import { decodeBlurhashToUrl } from "@/helpers/blurhash/decodeBlurhashToUrl";

/**
 * Decoding a blurhash spins up a canvas + toDataURL, which is too expensive to
 * run for every cell of a large grid synchronously on mount. This module caches
 * decoded data URLs by hash and decodes pending ones progressively during idle
 * time, so blurhashes still appear "immediately" without blocking navigation.
 */

const cache = new Map<string, string>();
const subscribers = new Map<string, Array<(url: string) => void>>();
const queue: string[] = [];
let scheduled = false;

function scheduleIdle(callback: () => void): void {
  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    window.requestIdleCallback(callback, { timeout: 500 });
  } else {
    setTimeout(callback, 16);
  }
}

function processQueue(): void {
  scheduled = false;
  const start = performance.now();

  // Decode in short slices (~8ms) to avoid long tasks that cause jank.
  while (queue.length > 0 && performance.now() - start < 8) {
    const hash = queue.shift()!;
    if (cache.has(hash)) continue;

    const url = decodeBlurhashToUrl(hash);
    if (url) {
      cache.set(hash, url);
      subscribers.get(hash)?.forEach((notify) => notify(url));
    }
    subscribers.delete(hash);
  }

  if (queue.length > 0) schedule();
}

function schedule(): void {
  if (scheduled) return;
  scheduled = true;
  scheduleIdle(processQueue);
}

/** Decode immediately (blocking). Use only for the few above-the-fold cells. */
export function getBlurhashUrlSync(hash: string | undefined): string | null {
  if (!hash) return null;
  const cached = cache.get(hash);
  if (cached) return cached;

  const url = decodeBlurhashToUrl(hash);
  if (url) cache.set(hash, url);
  return url;
}

/**
 * Resolve a blurhash URL without blocking: returns the cached value if present,
 * otherwise enqueues a background decode and invokes `onReady` once available.
 */
export function requestBlurhashUrl(
  hash: string | undefined,
  onReady: (url: string) => void,
): void {
  if (!hash) return;

  const cached = cache.get(hash);
  if (cached) {
    onReady(cached);
    return;
  }

  const subs = subscribers.get(hash) ?? [];
  subs.push(onReady);
  subscribers.set(hash, subs);
  queue.push(hash);
  schedule();
}
